import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { formBody, ind, jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import {
    billing_account_id_query,
    BillingAccount,
    BillingAccountCard,
    BillingAccountInvoice,
    BillingHistoryListResponse,
    BillingInvoicesListResponse,
    BillingListResponse,
    DocumentTypes,
    InvoiceId,
    invoicePdfResponse,
    RestrictionLevel,
    RestrictionLevelClearTopupThreshold,
} from "./billing.ts";
import {
    billing_account_id,
    datetime_epoch_s,
    datetime_z,
    int,
    limit_months_query,
    simpleSuccessResponse,
    user_id,
    uuid,
} from "./common.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

const InvoiceEmailSendingBody = z
    .object({
        billing_account_id,
        send_invoice_email: z.boolean(),
    })
    .openapi({ ref: "InvoiceEmailSendingBody" });

const CreditAmountBody = z
    .object({
        billing_account_id,
        amount: z.number(),
        description: z.string(),
    })
    .openapi({ ref: "CreditAmountBody" });

const DiscountPercentageBody = z
    .object({
        billing_account_id,
        discount_percentage: z.number(),
    })
    .openapi({ ref: "DiscountPercentageBody" });

const VatPercentageBody = z
    .object({
        billing_account_id,
        vat_percentage: z.number(),
    })
    .openapi({ ref: "VatPercentageBody" });

const ReferralShareCodeBody = z
    .object({
        billing_account_id,
        code: z
            .string()
            .describe('Letter case is ignored, e.g. "Cloud21" and "CLOUD21" refer to the same referral code.'),
    })
    .openapi({ ref: "ReferralShareCodeBody" });

const ReferralShareCodeResponse = z
    .object({
        referral_share_code: z.string(),
    })
    .openapi({ ref: "ReferralShareCodeResponse" });

const ActivateDeactivateBody = z
    .object({
        billing_account_id,
    })
    .openapi({ ref: "ActivateDeactivateBody" });

const DeleteBillingAccountBody = z
    .object({
        billing_account_id,
    })
    .openapi({ ref: "DeleteBillingAccountBody" });

const PayingByInvoiceBody = z
    .object({
        billing_account_id,
    })
    .openapi({ ref: "PayingByInvoiceBody" });

const DeleteBillingAccountResponse = z
    .object({
        success: z.boolean(),
        is_credit_note_created: z.boolean(),
    })
    .openapi({ ref: "DeleteBillingAccountResponse" });

const AccountClosingInvoicesResponses = z
    .object({
        success: z.boolean(),
        created_invoices_ids: z
            .array(z.number())
            .describe(
                "IDs of the invoices or reports created to close pending unpaid charges for the billing account.",
            ),
    })
    .openapi({ ref: "AccountClosingInvoicesResponses" });

const SuspensionReasonBody = z
    .object({
        billing_account_id,
        reason: z.string(),
    })
    .openapi({ ref: "SuspensionReasonBody" });

const RestrictionLevelThreshold = z
    .object({
        days: z
            .number()
            .describe(
                "Number of overdue days after which the billing account transitions to the configured restriction level.",
            ),
        restriction_level: RestrictionLevel,
    })
    .describe("Escalation rule for overdue post-payment billing accounts.")
    .openapi({ ref: "RestrictionLevelThreshold" });

const RestrictionLevelThresholds = z.array(RestrictionLevelThreshold).openapi({ ref: "RestrictionLevelThresholds" });

const GeneralSettings = z
    .object({
        first_account_credit: z
            .number()
            .describe("Credit granted to the first billing account created for a newly signed-up user."),
        min_topup_amount: z.number().describe("Minimum allowed top-up amount."),
        default_active_post_payment_level: RestrictionLevel.and(z.enum(["CLEAR", "LIMITED"])),
        restriction_level_clear_topup_threshold:
            RestrictionLevelClearTopupThreshold.shape.restriction_level_clear_topup_threshold,
        restriction_level_thresholds: RestrictionLevelThresholds,
        low_balance_notice_threshold: z.number().nullable(),
    })
    .openapi({ ref: "GeneralSettings" });

const FirstAccountCredit = z
    .object({
        first_account_credit: GeneralSettings.shape.first_account_credit,
    })
    .openapi({ ref: "FirstAccountCredit" });

const MinTopupAmount = z
    .object({
        min_topup_amount: GeneralSettings.shape.min_topup_amount,
    })
    .openapi({ ref: "MinTopupAmount" });

const DefaultActivePostPaymentLevel = z
    .object({
        default_active_post_payment_level: GeneralSettings.shape.default_active_post_payment_level,
    })
    .openapi({ ref: "DefaultActivePostPaymentLevel" });

const LowBalanceNoticeThreshold = z
    .object({
        low_balance_notice_threshold: GeneralSettings.shape.low_balance_notice_threshold,
    })
    .openapi({ ref: "LowBalanceNoticeThreshold" });

const LowBalanceNoticeThresholdBody = z
    .object({
        threshold: z.number().nullable().optional(),
    })
    .openapi({ ref: "LowBalanceNoticeThresholdBody" });

const UsageBasedRevenue = z
    .object({
        period_start_month: unref(datetime_z).describe("Month bucket start timestamp."),
        subtotal: z
            .number()
            .describe("Total amount for that usage bucket in the month, without discount, credit, or VAT adjustments."),
        type: z
            .enum(["cloud_services", "credited_without_vat"])
            .describe(
                "`cloud_services` covers regular cloud resource usage. `credited_without_vat` covers usage that was credited back, for example via billing-account deletion credit notes.",
            ),
        product_type: z.string().describe("Product/resource category when `split=true` is used.").optional(),
        location_slug: z.string().describe("Location code when the response is split by location.").optional(),
    })
    .openapi({ ref: "UsageBasedRevenue" });

const MonthlyArpa = z
    .object({
        period_start_month: unref(datetime_epoch_s).describe("Month bucket start timestamp."),
        paying_ba_count: z.number().describe("Number of paying billing accounts in the month."),
        arpa: z.number().describe("Average revenue per paying billing account for the month."),
    })
    .openapi({ ref: "MonthlyArpa" });

const CustomBillableProduct = z
    .object({
        uuid,
        description: z.string(),
        start_date: z.string(),
        end_date: z.string().nullable().optional(),
        billing_account_id,
        user_id,
        price_per_unit: z.number(),
        qty_unit: z.string(),
        creator_user_id: user_id.nullable().optional(),
        created_at: z.string(),
        updated_at: z.string().optional(),
    })
    .openapi({ ref: "CustomBillableProduct" });

const CustomBillableProductCreateBody = z
    .object({
        description: z.string(),
        start_date: z.string(),
        end_date: z.string().optional(),
        billing_account_id,
        price_per_unit: z.number(),
        qty_unit: z.string(),
    })
    .openapi({ ref: "CustomBillableProductCreateBody" });

const CustomBillableProductModifyBody = z
    .object({
        end_date: z.string().optional(),
    })
    .openapi({ ref: "CustomBillableProductModifyBody" });

const InvoiceRevenue = z
    .object({
        period_start_month: unref(datetime_epoch_s).describe("Month bucket start timestamp."),
        total_before_tax: z.number().describe("Total invoiced amount before VAT for the month."),
        vat_tax: z.number().describe("Total VAT amount invoiced for the month."),
        document_type: DocumentTypes,
    })
    .openapi({ ref: "InvoiceRevenue" });

const BillingAccountStats = z
    .object({
        total_first_billing_accounts: z.number().describe("How many users have created at least one billing account."),
        last_x_days_first_billing_accounts: z.array(
            z.object({
                days: z.number(),
                users: z.number().describe("Users who created their first billing account within the last N days."),
            }),
        ),
        first_billing_accounts_monthly: z.array(
            z.object({
                month: datetime_epoch_s,
                users: z.number().describe("Users who created their first billing account in that month."),
            }),
        ),
        total_financial_commitment: z
            .number()
            .describe(
                "How many users are financially committed. Pre-payment means first top-up completed; post-payment means first verified card added or invoice payment enabled.",
            ),
        last_x_days_financial_commitment: z.array(
            z.object({
                days: z.number(),
                users: z.number().describe("Users who became financially committed within the last N days."),
            }),
        ),
        financial_commitment_monthly: z.array(
            z.object({
                month: datetime_epoch_s,
                users: z.number().describe("Users who became financially committed in that month."),
            }),
        ),
    })
    .openapi({ ref: "BillingAccountStats" });

const level_override_query = z.string().openapi({ param: { name: "level_override", in: "query" } });

/* XXX: needs to be updated to billing_account_id in BE */
const billingAccountId_query = int.openapi({ param: { name: "billingAccountId", in: "query" } });

const invoice_status_update_query = z
    .enum(["paid", "unpaid"])
    .openapi({ ref: "InvoiceStatusUpdateParam", param: { name: "status", in: "query" } });

const allow_debt_query = z
    .boolean()
    .describe(
        "Filter by accounting model. `true` returns post-payment (`cloud_services`) data, `false` filters for pre-payment (`credit_topup`) data.",
    )
    .optional()
    .openapi({ param: { name: "allow_debt", in: "query" } });

const created_after_query = z
    .string()
    .describe("Lower inclusive date bound in `YYYY-MM-DD` format.")
    .optional()
    .openapi({ param: { name: "created_after", in: "query" } });

const created_before_query = z
    .string()
    .describe("Upper inclusive date bound in `YYYY-MM-DD` format.")
    .optional()
    .openapi({ param: { name: "created_before", in: "query" } });

const limit_query = int
    .min(1)
    .describe("Page size for paged billing account results.")
    .optional()
    .openapi({ param: { name: "limit", in: "query" } });

const page_query = int
    .min(1)
    .describe("1-based page number. Defaults to `1` when omitted.")
    .optional()
    .openapi({ param: { name: "page", in: "query" } });

const order_field_query = z
    .string()
    .describe("Field name to order billing accounts by.")
    .optional()
    .openapi({ param: { name: "order_field", in: "query" } });

const order_dir_query = z
    .enum(["asc", "desc"])
    .describe("Ordering direction. Defaults to `asc` when omitted.")
    .optional()
    .openapi({ param: { name: "order_dir", in: "query" } });

const filters_query = z
    .string()
    .describe(
        "URL-encoded JSON array of `{ field, value }` objects. Filters are combined with AND, text matching is case-insensitive, and values do not need to be exact matches.",
    )
    .optional()
    .openapi({ param: { name: "filters", in: "query" } });

const search_query = z
    .string()
    .describe(
        "URL-encoded free-text search term. Search spans text fields with OR semantics and does not require exact matches.",
    )
    .optional()
    .openapi({ param: { name: "search", in: "query" } });

const VatPercentages = z
    .record(z.string(), z.number())
    .describe(
        "VAT percentage configuration keyed by country code, with `DEFAULT` as the mandatory fallback for new billing accounts.",
    )
    .openapi({ ref: "VatPercentages" });

const BillingAccountMonthlyTotal = z
    .object({
        billing_account_id,
        discount_percentage: z.number().describe("Discount percentage configured for the billing account."),
        current_month_discount_amount: z
            .number()
            .describe("Actual discount amount calculated from current-month usage."),
        vat_percentage: z.number().describe("VAT percentage configured for the billing account."),
        current_month_vat_tax: z.number().describe("Actual VAT amount currently accrued for the month."),
        current_month_subtotal: z.number().describe("Current-month usage charges before credit, discount, and tax."),
        current_month_credit: z.number().describe("Amount of credit consumed in the current month."),
        current_month_total: z.number().describe("Current-month payable total including discount, credit, and VAT."),
        ongoing: z
            .number()
            .describe(
                "Running balance excluding VAT. Negative means amount owed so far this month; positive means remaining credit after current-month usage.",
            ),
        unpaid_invoices_total: z.number().describe("Sum of unpaid invoices, excluding current-month usage."),
        available_credit: z
            .number()
            .describe("Actual remaining credit on the account before current-month usage is applied."),
    })
    .openapi({ ref: "BillingAccountMonthlyTotal" });

export const billingAdminPaths = tagPaths("admin_billing")({
    "/payment/billing_account/all": {
        get: {
            summary: "List all billing accounts",
            description: ind`
                List all billing accounts on the platform.

                Query behavior:
                - Paging: use \`limit\` and \`page\`. When paging is enabled, the backend also returns \`X-Paging-Total\` response header metadata with the full item count.
                - Ordering: use \`order_field\` and optional \`order_dir\`.
                - Filtering: use \`filters\` as a URL-encoded JSON array like \`[{"field":"id","value":"1"},{"field":"title","value":"Primary Account"}]\`. Filters are combined with AND semantics.
                - Search: use \`search\` for broad case-insensitive text matching across text fields with OR semantics.
            `,
            parameters: [limit_query, page_query, order_field_query, order_dir_query, filters_query, search_query],
            responses: {
                ...successResponse(BillingListResponse),
            },
        },
    },

    /* Admin paths */
    "/payment/admin/billing_account/send_invoice_email": {
        put: {
            summary: "Enable or disable invoice emails",
            requestBody: jsonBody(InvoiceEmailSendingBody),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
    },

    "/payment/credit": {
        post: {
            summary: "Add or remove credit",
            description:
                "Add credit to a billing account or remove it by passing a negative `amount`. The `description` is stored as an admin note.",
            requestBody: jsonBody(CreditAmountBody),
            responses: {
                ...successResponse(BillingHistoryListResponse),
            },
        },
    },

    "/payment/admin/billing_account/discount": {
        put: {
            summary: "Update discount percentage",
            requestBody: jsonBody(DiscountPercentageBody),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
    },

    "/payment/admin/billing_account/vat": {
        put: {
            summary: "Update VAT percentage",
            description:
                "Update the VAT percentage for a billing account. This affects future invoices for that account.",
            requestBody: jsonBody(VatPercentageBody),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
    },

    "/payment/admin/billing_account/restriction_open_level_override": {
        put: {
            summary: "Update restriction level",
            parameters: [billing_account_id_query, level_override_query],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/payment/admin/billing_account/referral_share_code": {
        put: {
            summary: "Update referral share code",
            description: "Update the billing account's shareable referral code.",
            requestBody: jsonBody(ReferralShareCodeBody),
            responses: {
                ...successResponse(ReferralShareCodeResponse),
            },
        },
    },

    "/payment/billing_account/activate": {
        put: {
            summary: "Activate billing account",
            requestBody: jsonBody(ActivateDeactivateBody),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
    },

    "/payment/billing_account/deactivate": {
        put: {
            summary: "Deactivate billing account",
            requestBody: jsonBody(ActivateDeactivateBody),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
    },

    "/payment/admin/set_suspend_reason": {
        put: {
            summary: "Set suspension reason",
            description: "Set the free-form suspension reason shown for the billing account.",
            requestBody: jsonBody(SuspensionReasonBody),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
    },

    "/payment/paying_by_invoice": {
        post: {
            summary: "Add or verify invoice payment",
            description:
                "Create a `paying_by_invoice` payment method for the billing account or verify the existing one if it already exists.",
            requestBody: jsonBody(PayingByInvoiceBody),
            responses: {
                ...successResponse(BillingAccountCard),
            },
        },
        delete: {
            summary: "Mark invoice payment unverified",
            description:
                "Mark the billing account's `paying_by_invoice` payment method as unverified. Returns the updated payment object when one exists.",
            requestBody: jsonBody(PayingByInvoiceBody),
            responses: {
                ...successResponse(BillingAccountCard),
            },
        },
    },

    "/charging/admin/accountClosingInvoices": {
        post: {
            summary: "Generate closing invoices",
            description: ind`
                Generate closing invoices or usage reports for any pending unpaid charges on the billing account.

                This endpoint is intended to be called before deleting the billing account.

                For pre-payment accounts, overdrawn credit is not rewritten into current-month charges, which can result in generated reports with a positive total amount.
            `,
            parameters: [billingAccountId_query],
            responses: {
                ...successResponse(AccountClosingInvoicesResponses),
            },
        },
    },

    "/payment/admin/billing_account": {
        delete: {
            summary: "Delete billing account",
            description:
                "Delete the billing account, detach its payment methods, and create credit notes for unpaid invoices. This also deletes resources and services linked to the account. Generate closing invoices first to settle pending usage.",
            requestBody: jsonBody(DeleteBillingAccountBody),
            responses: {
                ...successResponse(DeleteBillingAccountResponse),
            },
        },
    },

    "/payment/admin/invoices": {
        get: {
            summary: "List all invoices",
            description: ind`
                List all generated invoices, reports, and credit notes.

                Date filters:
                - \`created_after\`: inclusive lower bound, for example \`2019-05-01\`.
                - \`created_before\`: inclusive upper bound, for example \`2021-10-31\`.

                It is recommended to use these bounds to reduce request time and response size.
            `,
            parameters: [created_after_query, created_before_query],
            responses: {
                ...successResponse(BillingInvoicesListResponse),
            },
        },
    },

    "/payment/admin/invoices/{invoice_id}": {
        get: {
            summary: "Get invoice details",
            description: "Get line items, totals, and payment details for a specific invoice.",
            parameters: [z.string().openapi({ param: { name: "invoice_id", in: "path" } })],
            responses: {
                ...successResponse(BillingAccountInvoice),
            },
        },
    },

    "/payment/admin/invoice/pdf": {
        get: {
            summary: "Download invoice PDF",
            parameters: [InvoiceId.openapi({ param: { name: "invoice_id", in: "query" } })],
            responses: {
                200: invoicePdfResponse,
            },
        },
    },

    // Admin
    "/config/payment": {
        get: {
            summary: "Get admin payment settings",
            description: "Returns all payment-related configuration that can be modified via the admin API.",
            responses: {
                ...successResponse(GeneralSettings),
            },
        },
    },
    "/config/payment/first_account_credit": {
        get: {
            summary: "Get first account credit",
            description:
                "Returns the amount of free credit granted to the first billing account when a new user signs up.",
            responses: {
                ...successResponse(FirstAccountCredit),
            },
        },
        put: {
            summary: "Update first account credit",
            description:
                "Updates the amount of free credit granted to the first billing account when a new user signs up.",
            requestBody: jsonBody(FirstAccountCredit),
            responses: {
                ...successResponse(FirstAccountCredit),
            },
        },
    },
    "/config/payment/min_topup_amount": {
        get: {
            summary: "Get minimum top-up amount",
            responses: {
                ...successResponse(MinTopupAmount),
            },
        },
        put: {
            summary: "Update min topup amount",
            requestBody: jsonBody(MinTopupAmount),
            responses: {
                ...successResponse(MinTopupAmount),
            },
        },
    },
    "/config/payment/default_active_post_payment_level": {
        get: {
            summary: "Get default active post-payment restriction level",
            description:
                "Returns the restriction level assigned when a frozen or terminated post-payment billing account is made active again.",
            responses: {
                ...successResponse(DefaultActivePostPaymentLevel),
            },
        },
        put: {
            summary: "Update default active post payment level",
            description:
                "Updates the restriction level assigned when a frozen or terminated post-payment billing account is made active again.",
            requestBody: jsonBody(DefaultActivePostPaymentLevel),
            responses: {
                ...successResponse(DefaultActivePostPaymentLevel),
            },
        },
    },
    "/config/payment/restriction_level_thresholds": {
        get: {
            summary: "Get restriction level thresholds",
            responses: {
                ...successResponse(RestrictionLevelThresholds),
            },
        },
        put: {
            summary: "Update restriction level thresholds",
            requestBody: jsonBody(RestrictionLevelThresholds),
            responses: {
                ...successResponse(RestrictionLevelThresholds),
            },
        },
    },
    "/config/payment/low_balance_notice_threshold": {
        put: {
            summary: "Update low balance notice threshold",
            requestBody: jsonBody(LowBalanceNoticeThresholdBody),
            responses: {
                ...successResponse(LowBalanceNoticeThreshold),
            },
        },
    },

    "/charging/admin/monthly_subtotals": {
        get: {
            summary: "Get usage based revenue",
            description:
                "Get month-by-month usage revenue. Results can be split by product dimensions with `split=true`, and are limited to the most recent `limit_months` months when provided.",
            parameters: [
                z
                    .boolean()
                    .optional()
                    .openapi({ param: { name: "split", in: "query" } }),
                limit_months_query,
            ],
            responses: {
                ...successResponse(z.array(UsageBasedRevenue)),
            },
        },
    },

    "/charging/admin/all_totals": {
        get: {
            summary: "List billing account monthly totals",
            description: ind`
                List current-month billing totals for every billing account.

                General formula:
                - Total = Subtotal - Discount - Credit + VAT
            `,
            responses: {
                ...successResponse(z.array(BillingAccountMonthlyTotal)),
            },
        },
    },

    "/payment/admin/invoiced_revenue": {
        get: {
            summary: "Get invoice revenue",
            parameters: [limit_months_query],
            responses: {
                ...successResponse(z.array(InvoiceRevenue)),
            },
        },
    },

    "/payment/admin/billing_account_stats": {
        get: {
            summary: "Get billing account stats",
            description:
                "Get statistics for users who created their first billing account and users who became financially committed. `limit_months` limits the monthly and recent-history buckets only; it does not affect all-time totals.",
            parameters: [limit_months_query],
            responses: {
                ...successResponse(BillingAccountStats),
            },
        },
    },

    "/payment/admin/download_digio_kyc_report": {
        get: {
            summary: "Download Digio KYC report",
            description: "Download the Digio PDF KYC report for a billing account.",
            parameters: [billing_account_id_query],
            responses: {
                200: { description: "KYC report PDF content", content: { "application/pdf": {} } },
            },
        },
    },

    "/payment/admin/monthly_arpa": {
        get: {
            summary: "Get monthly ARPA",
            description: "Get monthly counts of paying billing accounts and ARPA.",
            parameters: [created_after_query, allow_debt_query],
            responses: {
                ...successResponse(z.array(MonthlyArpa)),
            },
        },
    },

    "/charging/admin/products": {
        get: {
            summary: "Get Custom Billable Products",
            parameters: [
                billing_account_id.optional().openapi({
                    param: { name: "billing_account_id", in: "query" },
                }),
            ],
            responses: {
                ...successResponse(z.array(CustomBillableProduct)),
            },
        },
        post: {
            summary: "Create Custom Billable Product",
            requestBody: formBody(CustomBillableProductCreateBody),
            responses: {
                ...successResponse(CustomBillableProduct),
            },
        },
    },

    "/charging/admin/products/{uuid}": {
        patch: {
            summary: "Update Custom Billable Product",
            requestBody: formBody(CustomBillableProductModifyBody),
            parameters: [params.uuid],
            responses: {
                ...successResponse(CustomBillableProduct),
            },
        },
    },

    "/payment/admin/credit": {
        get: {
            summary: "Get billing account credit history",
            description:
                "Get detailed billing account credit history, including admin-added credit and consumed credit. Set `allow_deleted=true` to include deleted records.",
            parameters: [
                billing_account_id_query,
                z
                    .boolean()
                    .optional()
                    .openapi({ param: { name: "allow_deleted", in: "query" } }),
            ],
            responses: {
                ...successResponse(BillingHistoryListResponse),
            },
        },
    },

    /* Update invoice status */
    "/payment/admin/invoice/status": {
        put: {
            summary: "Update invoice status",
            description: "Set an invoice document number to `paid` or `unpaid`.",
            parameters: [
                z.string().openapi({ param: { name: "document_number", in: "query" } }),
                invoice_status_update_query,
            ],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/payment/admin/vat_percentages": {
        get: {
            summary: "Get VAT percentages",
            description:
                "Get the VAT percentage configuration. The `DEFAULT` key is used for new billing accounts, and country-code keys override it for specific countries.",
            responses: {
                ...successResponse(VatPercentages),
            },
        },
        put: {
            summary: "Update VAT percentages",
            description:
                "Replace the VAT percentage configuration. `DEFAULT` is required and country-specific overrides are optional.",
            requestBody: jsonBody(VatPercentages),
            responses: {
                ...successResponse(VatPercentages),
            },
        },
    },
});
